<template>
  <el-dialog
    title="AI 智能运维专家 (Agent Mode)"
    :visible.sync="visible"
    width="95%"
    top="5vh"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    append-to-body
    custom-class="agent-dialog"
    @close="handleClose"
  >
    <div class="agent-container">
      <!-- Left Panel: Agent Stream / Timeline -->
      <div class="agent-stream-panel">
        <div class="stream-header">
           <div class="agent-avatar">
             <i class="el-icon-s-custom" style="font-size: 24px;"></i>
           </div>
           <div class="agent-status">
             <div class="name">DevOps Copilot</div>
             <div class="status-text" v-if="isThinking">
               <i class="el-icon-loading"></i> 思考分析中...
             </div>
             <div class="status-text idle" v-else>
               就绪
             </div>
           </div>
        </div>

        <div class="stream-content" ref="streamContent">
           <div v-for="(event, index) in eventStream" :key="index" class="stream-item">
              
              <!-- Type: Thought (Internal Monologue) -->
              <div v-if="event.type === 'thought'" class="agent-thought">
                 <div class="thought-icon"><i class="el-icon-cpu"></i></div>
                 <div class="thought-content">{{ event.content }}</div>
              </div>

              <!-- Type: Message (Chat) -->
              <div v-if="event.type === 'message'" class="agent-message">
                 <div class="message-bubble">
                    {{ event.content }}
                 </div>
              </div>

              <!-- Type: Analysis Plan (Structured Output) -->
              <div v-if="event.type === 'analysis_plan'" class="agent-plan">
                 <div class="plan-card">
                    <div class="plan-header">
                       <i class="el-icon-document"></i> 排查报告
                    </div>
                    <div class="plan-body">
                       <div v-if="event.summary" class="plan-summary">
                         <div class="plan-section-title">结论</div>
                         <div class="plan-summary-text">{{ event.summary }}</div>
                       </div>
                       <div v-if="event.trace && event.trace.length" class="plan-trace">
                         <div class="plan-section-title">排查过程</div>
                         <div v-for="(trace, traceIndex) in event.trace" :key="traceIndex" class="trace-item">
                           <div class="trace-index">{{ traceIndex + 1 }}</div>
                           <div class="trace-content">
                             <div v-if="trace.check" class="trace-check">{{ trace.check }}</div>
                             <div v-if="trace.evidence" class="trace-evidence">证据：{{ trace.evidence }}</div>
                             <div v-if="trace.result" class="trace-result">结论：{{ trace.result }}</div>
                           </div>
                         </div>
                       </div>
                       <div v-for="(step, stepIndex) in event.steps" :key="stepIndex" class="step-item">
                          <div class="step-num">{{ stepIndex + 1 }}</div>
                          <div class="step-content">
                             <div v-if="step.title" class="step-title">{{ step.title }}</div>
                             <div v-if="step.command" class="step-cmd">
                                <code>{{ step.command }}</code>
                                <div class="step-actions">
                                  <el-button
                                    size="mini"
                                    type="primary"
                                    :disabled="!isConnected"
                                    @click="runStepCommand(step)"
                                  >
                                    执行
                                  </el-button>
                                </div>
                             </div>
                             <div v-if="step.description" class="step-desc">{{ step.description }}</div>
                          </div>
                       </div>
                    </div>
                 </div>
              </div>

              <!-- Type: Tool Call (Action) -->
              <div v-if="event.type === 'tool_call'" class="agent-action">
                 <div class="action-card">
                    <div class="action-header">
                       <i class="el-icon-monitor"></i> 执行指令
                    </div>
                    <div class="action-code">
                       <code>{{ event.input }}</code>
                    </div>
                    <div class="action-status">
                       <span v-if="event.status === 'pending'" class="status-pending"><i class="el-icon-time"></i> 待执行</span>
                       <span v-if="event.status === 'running'" class="status-running"><i class="el-icon-loading"></i> 执行中...</span>
                       <span v-if="event.status === 'success'" class="status-success"><i class="el-icon-check"></i> 完成</span>
                       <span v-if="event.status === 'failed'" class="status-failed"><i class="el-icon-close"></i> 失败</span>
                    </div>
                    <div class="action-actions">
                       <el-button
                         size="mini"
                         type="primary"
                         :disabled="!event.input || !isConnected || !_isShellTool(event.tool)"
                         @click="runToolCommand(event)"
                       >
                         执行
                       </el-button>
                    </div>
                 </div>
              </div>

              <!-- Type: Tool Output -->
              <div v-if="event.type === 'tool_output'" class="agent-tool-output">
                 <div class="output-header">
                   <i class="el-icon-document"></i> Output
                 </div>
                 <pre class="output-body">{{ event.content }}</pre>
              </div>

              <!-- Type: User Input (Human Instruction) -->
              <div v-if="event.type === 'user_input'" class="user-input-message">
                 <div class="message-bubble user">
                    <i class="el-icon-user"></i> {{ event.content }}
                 </div>
              </div>
           </div>
        </div>
        
        <!-- User Input Area - Human Instruction Intervention -->
        <div class="input-area">
           <el-input
             :placeholder="isPaused ? 'Paused. Press Esc to resume...' : 'Human instruction (Enter to send, Esc to pause)...'"
             v-model="userInput"
             :disabled="!canTypeUserMessage"
             @keyup.enter.native="sendUserMessage"
             @keyup.esc.native="togglePause"
           >
              <el-button
                slot="append"
                icon="el-icon-s-promotion"
                :disabled="!userInput.trim() || !canSendUserMessage"
                @click="sendUserMessage"
              ></el-button>
           </el-input>
        </div>
      </div>

      <!-- Right Panel: Live Terminal -->
      <div class="terminal-panel">
        <div class="panel-header">
          <span class="title"> <i class="el-icon-s-platform"></i> 实时终端 ({{ alertInfo.hostip || 'Server' }})</span>
          <div class="controls">
            <el-tag size="mini" type="success" v-if="isConnected">已连接</el-tag>
            <el-tag size="mini" type="info" v-else>未连接</el-tag>
          </div>
        </div>
        <div class="terminal-wrapper">
             <shell_term
                ref="shellTerm"
                :init_token="shellToken"
                show_type="0"
             ></shell_term>
             <!-- Overlay for "Connecting" state if needed -->
             <div v-if="!isConnected && !shellToken" class="terminal-overlay">
                <div class="overlay-content">等待 Agent 连接服务器...</div>
             </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import * as API from "@/api/index.js";
import shell_term from "@/components/webshell/shell_term";

export default {
  name: "AiAnalysisDialog",
  components: {
    shell_term,
  },
  data() {
    return {
      visible: false,
      alertInfo: {},
      userInput: "",
      
      // Agent State
      isThinking: false,
      eventStream: [],
      
      // Terminal State
      shellToken: "",
      isConnected: false,
      
      // Stream State
      streamController: null,
      lastToolIndex: -1,
      userQueryEvidence: null,
      planAdded: false,
      planIndex: -1,
      streamingMessageIndex: -1,
      jsonStreamBuffer: "",
      jsonStreamActive: false,

      // WebSocket State (for human instruction intervention)
      websocket: null,
      sessionId: null,
      isPaused: false,
      useWebSocket: true, // Toggle between WebSocket and SSE mode
    };
  },
  computed: {
    canTypeUserMessage() {
      return this.visible && !this.isThinking;
    },
    canSendUserMessage() {
      return (
        this.useWebSocket &&
        this.websocket &&
        this.websocket.readyState === WebSocket.OPEN
      );
    },
  },
  methods: {
    open(alertItem) {
      this.stopStream();
      this.alertInfo = alertItem || {};
      this.visible = true;
      this.eventStream = [];
      this.isThinking = false;
      this.isConnected = false;
      this.shellToken = ""; // Clear token
      this.lastToolIndex = -1;
      this.userQueryEvidence = null;
      this.planAdded = false;
      this.planIndex = -1;
      this.streamingMessageIndex = -1;
      this.jsonStreamBuffer = "";
      this.jsonStreamActive = false;
      this.websocket = null;
      this.sessionId = null;
      this.isPaused = false;

      this.$nextTick(() => {
          this.startAnalysis();
      });
    },

    handleClose() {
        this.visible = false;
        this.stopStream();
        this.closeWebSocket();
        this.closeShell();
    },

    // --- Core Agent Logic (Backend Streaming) ---
    async startAnalysis() {
        this.stopStream();
        this.isThinking = true;
        this.lastToolIndex = -1;
        this.isPaused = false;

        await this.connectShell();

        // Use WebSocket for human instruction intervention support
        if (this.useWebSocket) {
            this.connectWebSocket();
        } else {
            await this.startStream();
        }
    },

    stopStream() {
        // SSE mode cleanup
        if (this.streamController) {
            this.streamController.abort();
            this.streamController = null;
        }
        // WebSocket mode cleanup
        this.cancelAnalysis();
        this.isThinking = false;
    },

    async startStream() {
        const controller =
            typeof AbortController !== "undefined" ? new AbortController() : null;
        this.streamController = controller;

        const payload = this._buildAnalyzePayload();
        try {
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "text/event-stream"
                },
                body: JSON.stringify(payload),
            };
            if (controller) {
                options.signal = controller.signal;
            }
            const response = await fetch(this._getAnalyzeUrl(), options);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            if (!response.body) {
                throw new Error("Streaming not supported");
            }

            await this._readSseStream(response.body);
        } catch (err) {
            if (err && err.name === "AbortError") {
                return;
            }
            const message = err && err.message ? err.message : String(err);
            this._appendEvent({ type: "message", content: "Agent stream error: " + message });
        } finally {
            if (this.streamController === controller) {
                this.streamController = null;
            }
            this.isThinking = false;
        }
    },

    async _readSseStream(body) {
        const reader = body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        while (true) {
            const result = await reader.read();
            if (result.done) {
                break;
            }
            buffer += decoder.decode(result.value, { stream: true });
            buffer = buffer.replace(/\r\n/g, "\n");

            let boundary = buffer.indexOf("\n\n");
            while (boundary !== -1) {
                const chunk = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);
                this._handleSseChunk(chunk);
                boundary = buffer.indexOf("\n\n");
            }
        }

        if (buffer.trim()) {
            this._handleSseChunk(buffer);
        }
    },

    _handleSseChunk(chunk) {
        const lines = chunk.split("\n");
        let eventType = "message";
        const dataLines = [];

        for (const line of lines) {
            if (line.startsWith("event:")) {
                eventType = line.slice(6).trim();
                continue;
            }
            if (line.startsWith("data:")) {
                dataLines.push(line.slice(5).trim());
            }
        }

        const dataText = dataLines.join("\n");
        const data = this._safeJsonParse(dataText);
        this._handleAgentEvent(eventType, data);
    },

    _handleAgentEvent(eventType, data) {
        if (eventType === "thought") {
            if (data) {
                this._appendEvent({ type: "thought", content: String(data) });
            }
            return;
        }

        // Handle user_message_processed event (from WebSocket agent_event)
        if (eventType === "user_message_processed") {
            if (data) {
                this._appendEvent({
                    type: "thought",
                    content: "Processing instruction: " + String(data),
                });
            }
            return;
        }

        if (eventType === "message_delta") {
            const text = this._normalizeMessageDelta(data);
            if (text) {
                if (this._consumeJsonFragment(text)) {
                    return;
                }
                if (this._handleJsonTextMessage(text)) {
                    return;
                }
                this._appendMessageDelta(text);
            }
            return;
        }

        // Ignore draft plan from StructuredOutput; use final verified payload only.
        if (eventType === "tool_start" && data && (data.tool === "StructuredOutput" || data.tool === "structured_output")) {
             return;
        }

        if (eventType === "tool_start") {
            const inputText = this._formatToolInput(data);
            const toolName = data && typeof data === "object" ? data.tool || "" : "";
            this._appendEvent({
                type: "tool_call",
                tool: toolName,
                input: inputText,
                status: "pending",
            });
            this.lastToolIndex = this.eventStream.length - 1;
            return;
        }

        if (eventType === "tool_output") {
            const toolName = data && typeof data === "object" ? data.tool || "" : "";
            // Special handling: Ignore output for StructuredOutput
            if (toolName === "StructuredOutput" || toolName === "structured_output") return;

            const status = data && data.is_error ? "failed" : "success";
            const index = this._findLastToolCallIndex();
            if (index !== -1) {
                this.$set(this.eventStream[index], "status", status);
            }
            
            const output = data && typeof data === "object" ? data.output : "";
            const outputText =
                typeof output === "string" ? output : JSON.stringify(output);
            if (outputText) {
                if (index !== -1) {
                    this.$set(this.eventStream[index], "output", outputText);
                }
                this._appendEvent({
                    type: "tool_output",
                    tool: toolName,
                    content: outputText,
                });
            }
            return;
        }

        if (eventType === "final") {
            // 调试日志
            console.log("[DEBUG] Received final event:", data);
            console.log("[DEBUG] planIndex before cleanup:", this.planIndex);

            // 在添加排查报告之前，清理掉可能泄漏的 JSON 片段消息
            this._cleanupLeakedJsonMessages();

            const plan = this._buildPlanEvent(data);
            console.log("[DEBUG] Built plan:", plan);
            console.log("[DEBUG] planIndex before append:", this.planIndex);

            this._appendPlanEvent(plan);

            console.log("[DEBUG] planIndex after append:", this.planIndex);
            console.log("[DEBUG] eventStream length:", this.eventStream.length);

            this.isThinking = false;
            this.streamingMessageIndex = -1;
            this.jsonStreamBuffer = "";
            this.jsonStreamActive = false;
            return;
        }

        if (eventType === "error") {
            const message = data && data.message ? data.message : "Agent error";
            this._appendEvent({ type: "message", content: "Agent error: " + message });
            this.isThinking = false;
            this.streamingMessageIndex = -1;
            return;
        }

        // Auto-detect structured plan in generic messages
        if (data && typeof data === 'object' && data.analysis_summary && Array.isArray(data.steps)) {
             const plan = this._buildPlanEvent(data);
             if (plan) {
                 this._appendPlanEvent(plan);
                 return;
             }
        }

        if (data && typeof data === "object" && this._handleToolPayloadObject(data)) {
            return;
        }

        if (data) {
            const message = typeof data === "string" ? data : JSON.stringify(data);
            if (this._handleJsonTextMessage(message)) {
                return;
            }
            // Filter out redundant completion messages
            if (
                (message.includes("分析结束") || message.includes("排查结束")) &&
                (message.includes("告警已恢复") || message.includes("告警未恢复"))
            ) {
                return;
            }
            // Filter out raw JSON fragments that leaked into message
            if (this._isLeakedJsonData(message)) {
                 return;
            }
            this._appendEvent({ type: "message", content: message });
        }
    },

    _isLeakedJsonData(message) {
        if (!message || typeof message !== "string") {
            return false;
        }
        const trimmed = message.trim();

        // 过滤以 JSON 对象格式开头的内容
        if (trimmed.startsWith("{") && trimmed.length > 2) {
            return true;
        }

        // 过滤排查报告相关的 JSON 字段（带引号）
        if (trimmed.includes('"analysis_summary"') ||
            trimmed.includes('"analysis_trace"') ||
            trimmed.includes('"steps":[') ||
            trimmed.includes('\\"analysis_summary\\"') ||
            trimmed.includes('\\"analysis_trace\\"')) {
            return true;
        }

        // 过滤排查报告相关的 JSON 字段（不带引号，JSON 被错误解析后的结果）
        if (trimmed.includes('analysis_summary') ||
            trimmed.includes('analysis_trace') ||
            trimmed.includes('analysis_tracecheck')) {
            return true;
        }

        // 过滤以 JSON 对象格式开头且包含排查报告结构的内容
        if (trimmed.startsWith("{") && (
            trimmed.includes("summary") ||
            trimmed.includes("trace") ||
            trimmed.includes("check") ||
            trimmed.includes("evidence")
        )) {
            // 尝试解析并检查是否是排查报告结构
            try {
                const parsed = JSON.parse(trimmed);
                if (parsed && typeof parsed === "object" &&
                    (parsed.analysis_summary || parsed.analysis_trace || parsed.steps)) {
                    return true;
                }
            } catch (e) {
                // 不是有效 JSON，可能是片段，继续检查
            }
        }

        // 过滤 StructuredOutput 工具相关的内容
        if (trimmed.includes("StructuredOutput") || trimmed.includes("structured_output")) {
            return true;
        }

        // 过滤 JSON 控制标记
        const lower = trimmed.toLowerCase();
        if (lower.includes("begin strict json output") ||
            lower.includes("end strict json output") ||
            lower.includes("```json")) {
            return true;
        }

        // 过滤 JSON 键值对模式（不带引号，如 analysis_summary "xxx"）
        if (/^[a-z_]+\s+"/.test(trimmed)) {
            return true;
        }

        return false;
    },

    _cleanupLeakedJsonMessages() {
        // 从后往前遍历 eventStream，移除可能泄漏的 JSON 片段消息
        // 只清理最近的消息（最多检查最后 10 条）
        const maxCheck = Math.min(10, this.eventStream.length);
        const indicesToRemove = [];

        for (let i = this.eventStream.length - 1; i >= this.eventStream.length - maxCheck && i >= 0; i--) {
            const event = this.eventStream[i];
            if (!event) continue;

            // 只检查 message 类型的事件
            if (event.type !== "message") continue;

            const content = String(event.content || "").trim();
            if (!content) continue;

            // 检查是否是 JSON 片段
            if (this._isJsonFragmentContent(content)) {
                indicesToRemove.push(i);
            }
        }

        // 从后往前删除，避免索引偏移问题
        indicesToRemove.sort((a, b) => b - a);
        for (const index of indicesToRemove) {
            this.eventStream.splice(index, 1);
        }

        // 重置相关索引
        if (indicesToRemove.length > 0) {
            this.streamingMessageIndex = -1;
            // 更新 planIndex（如果有的话）
            if (this.planIndex !== -1) {
                const removed = indicesToRemove.filter(i => i < this.planIndex).length;
                this.planIndex -= removed;
            }
        }
    },

    _isJsonFragmentContent(content) {
        if (!content) return false;

        // 以 JSON 对象开头
        if (content.startsWith("{")) return true;

        // 包含排查报告的 JSON 字段（带引号）
        if (content.includes('"analysis_summary"') ||
            content.includes('"analysis_trace"') ||
            content.includes('"steps"')) {
            return true;
        }

        // 包含排查报告的 JSON 字段（不带引号，直接作为文本出现）
        // 这种情况是 JSON 被错误解析后的结果
        if (content.includes('analysis_summary') ||
            content.includes('analysis_trace') ||
            content.includes('analysis_tracecheck')) {
            return true;
        }

        // 包含转义的 JSON 字段
        if (content.includes('\\"analysis_summary\\"') ||
            content.includes('\\"analysis_trace\\"')) {
            return true;
        }

        // JSON 键值对模式（带引号）
        if (/^"[a-z_]+":\s*/.test(content)) return true;

        // JSON 键值对模式（不带引号，如 analysis_summary "xxx"）
        if (/^[a-z_]+\s+"/.test(content)) return true;

        // JSON 控制标记
        const lower = content.toLowerCase();
        if (lower.includes("begin strict json output") ||
            lower.includes("```json")) {
            return true;
        }

        return false;
    },

    _appendEvent(event) {
        this.eventStream.push(event);
        this._scrollStream();
    },

    _appendMessageDelta(text) {
        // 过滤掉可能泄漏的 JSON 片段
        if (this._shouldFilterMessageDelta(text)) {
            return;
        }

        if (this.streamingMessageIndex === -1) {
            this._appendEvent({ type: "message", content: text, streaming: true });
            this.streamingMessageIndex = this.eventStream.length - 1;
            return;
        }
        const index = this.streamingMessageIndex;
        const item = this.eventStream[index];
        if (!item || item.type !== "message") {
            this.streamingMessageIndex = -1;
            this._appendEvent({ type: "message", content: text, streaming: true });
            this.streamingMessageIndex = this.eventStream.length - 1;
            return;
        }

        // 检查累积内容是否变成了 JSON 片段
        const newContent = String(item.content || "") + text;
        if (this._looksLikeAnalysisPlanJson(newContent)) {
            // 移除当前正在流式传输的消息，因为它是 JSON 数据
            this.eventStream.splice(index, 1);
            this.streamingMessageIndex = -1;
            // 尝试将累积的内容作为 JSON 处理
            this._consumeJsonFragment(newContent);
            return;
        }

        const updated = Object.assign({}, item, {
            content: newContent,
        });
        this.$set(this.eventStream, index, updated);
        this._scrollStream();
    },

    _shouldFilterMessageDelta(text) {
        if (!text || typeof text !== "string") {
            return false;
        }
        const trimmed = text.trim();

        // 过滤以 JSON 对象开头的文本
        if (trimmed.startsWith("{") && trimmed.includes('"')) {
            return true;
        }

        // 过滤包含排查报告关键字段的文本（带引号）
        if (trimmed.includes('"analysis_summary"') ||
            trimmed.includes('"analysis_trace"') ||
            trimmed.includes('\\"analysis_summary\\"') ||
            trimmed.includes('\\"analysis_trace\\"')) {
            return true;
        }

        // 过滤包含排查报告关键字段的文本（不带引号）
        // 这种情况是 JSON 被错误解析后的结果
        if (trimmed.includes('analysis_summary') ||
            trimmed.includes('analysis_trace') ||
            trimmed.includes('analysis_tracecheck')) {
            return true;
        }

        // 过滤明显的 JSON 片段（键值对模式，带引号）
        if (/^"[a-z_]+"\s*:\s*/.test(trimmed) || /^\s*{\s*"[a-z_]+"\s*:/.test(trimmed)) {
            return true;
        }

        // 过滤明显的 JSON 片段（键值对模式，不带引号）
        if (/^[a-z_]+\s+"/.test(trimmed)) {
            return true;
        }

        return false;
    },

    _looksLikeAnalysisPlanJson(text) {
        if (!text || typeof text !== "string") {
            return false;
        }
        const trimmed = text.trim();

        // 检查是否像是排查报告的 JSON 结构
        if (!trimmed.startsWith("{")) {
            return false;
        }

        // 检查是否包含排查报告的关键字段
        return (
            trimmed.includes('"analysis_summary"') ||
            trimmed.includes('"analysis_trace"') ||
            trimmed.includes('"steps"') ||
            trimmed.includes('\\"analysis_summary\\"') ||
            trimmed.includes('\\"analysis_trace\\"')
        );
    },

    _scrollStream() {
        this.$nextTick(() => {
            const container = this.$refs.streamContent;
            if (container) container.scrollTop = container.scrollHeight;
        });
    },

    _findLastToolCallIndex() {
        for (let i = this.eventStream.length - 1; i >= 0; i -= 1) {
            const item = this.eventStream[i];
            if (
                item &&
                item.type === "tool_call" &&
                (item.status === "running" || item.status === "pending")
            ) {
                return i;
            }
        }
        return -1;
    },

    _formatToolInput(data) {
        if (!data || typeof data !== "object") {
            return "";
        }
        const input = data.input;
        if (!input) {
            return "";
        }
        if (typeof input === "string") {
            return input;
        }
        if (input.command) {
            return String(input.command);
        }
        try {
            return JSON.stringify(input);
        } catch (err) {
            return "";
        }
    },

    _getAnalyzeUrl() {
        const override =
            (typeof window !== "undefined" && window.__AI_ANALYZE_URL__) || "";
        if (override) {
            return String(override);
        }
        if (process.env.NODE_ENV === "development") {
            const protocol = window.location.protocol || "http:";
            const host = window.location.hostname || "127.0.0.1";
            return `${protocol}//${host}:8001/analyze`;
        }
        return "/analyze";
    },

    runToolCommand(event) {
        if (!event || !event.input) {
            return;
        }
        if (!this._isShellTool(event.tool)) {
            this.$message.warning("仅支持在终端执行 shell 指令");
            return;
        }
        const command = this._normalizeCommand(event.input);
        if (!command) {
            return;
        }
        this._sendCommandToTerminal(command);
        this._mirrorToolOutputToTerminal(event.output);
        
        // Set to running initially
        this.$set(event, "status", "running");
        
        // Since we cannot reliably detect when the command finishes in the raw shell terminal
        // (no exit code or prompt detection available from the dumb PTY stream),
        // we automatically set status to 'success' after a short delay to prevent
        // the "Executing..." state from persisting indefinitely.
        setTimeout(() => {
            this.$set(event, "status", "success");
        }, 1000);
    },

    runStepCommand(step) {
        if (!step || !step.command) {
            return;
        }
        const command = this._normalizeCommand(step.command);
        if (!command) {
            return;
        }
        this._sendCommandToTerminal(command);
        const cachedOutput = this._findCachedToolOutput(command);
        this._mirrorToolOutputToTerminal(cachedOutput);
    },

    _sendCommandToTerminal(command) {
        const shell = this.$refs.shellTerm;
        if (!shell) {
            return;
        }
        if (typeof shell.sendCommand === "function") {
            shell.sendCommand(command);
            return;
        }
        if (typeof shell.on_send === "function") {
            shell.on_send(command + "\r");
            return;
        }
        this.typeInTerminal(command);
    },

    _normalizeCommand(command) {
        return String(command || "").trim();
    },

    _findCachedToolOutput(command) {
        const target = this._normalizeCommand(command);
        if (!target) {
            return "";
        }
        for (let i = this.eventStream.length - 1; i >= 0; i -= 1) {
            const item = this.eventStream[i];
            if (item && item.type === "tool_call") {
                const input = this._normalizeCommand(item.input);
                if (input === target && item.output) {
                    return item.output;
                }
            }
        }
        return "";
    },

    _mirrorToolOutputToTerminal(output) {
        if (!output) {
            return;
        }
        const text = String(output).trim();
        if (!text) {
            return;
        }
        this._writeTerminalOutput(text);
    },

    _appendPlanEvent(plan) {
        if (!plan) {
            return;
        }
        if (this.planIndex !== -1) {
            const existing = this.eventStream[this.planIndex] || {};
            const updated = Object.assign({}, existing, plan, { type: "analysis_plan" });
            this.$set(this.eventStream, this.planIndex, updated);
            return;
        }
        this._appendEvent(plan);
        this.planAdded = true;
        this.planIndex = this.eventStream.length - 1;
    },

    _buildPlanEvent(data) {
        if (!data || typeof data !== "object") {
            return null;
        }
        const summary = data.analysis_summary
            ? String(data.analysis_summary).trim()
            : "";
        const trace = this._normalizeTrace(
            data.analysis_trace || data.trace || data.investigation
        );
        const steps = this._normalizePlanSteps(data.steps);
        if (!summary && !steps.length && !trace.length) {
            return null;
        }
        return {
            type: "analysis_plan",
            summary: summary,
            trace: trace,
            steps: steps,
        };
    },

    _normalizePlanSteps(rawSteps) {
        if (!Array.isArray(rawSteps)) {
            return [];
        }
        const normalized = [];
        rawSteps.forEach((step) => {
            if (!step || typeof step !== "object") {
                return;
            }
            const title = step.title || step.Title || "";
            const command = step.command || step.Command || "";
            const description =
                step.description || step.Description || step.desc || "";
            const cleanTitle = String(title).trim();
            const cleanCommand = String(command).trim();
            const cleanDescription = String(description).trim();
            if (!cleanTitle && !cleanCommand && !cleanDescription) {
                return;
            }
            normalized.push({
                title: cleanTitle,
                command: cleanCommand,
                description: cleanDescription,
            });
        });
        return normalized;
    },

    _normalizeTrace(rawTrace) {
        if (!Array.isArray(rawTrace)) {
            return [];
        }
        const normalized = [];
        rawTrace.forEach((item) => {
            if (!item || typeof item !== "object") {
                return;
            }
            const check = item.check || item.title || item.item || "";
            const evidence = item.evidence || item.observation || item.detail || "";
            const result = item.result || item.conclusion || item.status || "";
            const cleanCheck = String(check).trim();
            const cleanEvidence = String(evidence).trim();
            const cleanResult = String(result).trim();
            if (!cleanCheck && !cleanEvidence && !cleanResult) {
                return;
            }
            normalized.push({
                check: cleanCheck,
                evidence: cleanEvidence,
                result: cleanResult,
            });
        });
        return normalized;
    },

    _isShellTool(toolName) {
        if (!toolName) return false;
        const lower = toolName.toLowerCase();
        return lower.includes('shell') || lower.includes('cmd') || lower.includes('exec');
    },

    // =============================================================================
    // WebSocket Methods - Human Instruction Intervention Support
    // =============================================================================

    connectWebSocket() {
        const wsUrl = this._getWebSocketUrl();
        console.log("Connecting WebSocket:", wsUrl);

        this.websocket = new WebSocket(wsUrl);

        this.websocket.onopen = () => {
            console.log("WebSocket connected");
            // Send start analysis request
            this.websocket.send(JSON.stringify({
                type: "start",
                alert_msg: this.alertInfo.alert_msg || this.alertInfo.alertitem_desc || "",
                host_ip: this.alertInfo.hostip || this.alertInfo.server_ip || "",
                server_details: this._buildServerDetails(),
                shell_token: this.shellToken,
            }));
        };

        this.websocket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this._handleWebSocketMessage(data);
            } catch (e) {
                console.error("Failed to parse WebSocket message:", e);
            }
        };

        this.websocket.onerror = (error) => {
            console.error("WebSocket error:", error);
            this._appendEvent({
                type: "message",
                content: "WebSocket connection error"
            });
        };

        this.websocket.onclose = () => {
            console.log("WebSocket closed");
            this.isThinking = false;
            this.websocket = null;
        };
    },

    _handleWebSocketMessage(data) {
        switch (data.type) {
            case "session_created":
                this.sessionId = data.session_id;
                console.log("Session created:", this.sessionId);
                break;

            case "agent_event":
                // Reuse existing event handling logic
                this._handleAgentEvent(data.event, data.data);
                break;

            case "message_received":
                // Show user message received confirmation
                this._appendEvent({
                    type: "user_input",
                    content: data.message,
                });
                break;

            case "completed":
                this.isThinking = false;
                break;

            case "cancelled":
                this.isThinking = false;
                this._appendEvent({
                    type: "message",
                    content: "Analysis cancelled",
                });
                break;

            case "paused":
                this.isPaused = true;
                this._appendEvent({
                    type: "message",
                    content: "Analysis paused. Press Esc to resume.",
                });
                break;

            case "resumed":
                this.isPaused = false;
                this._appendEvent({
                    type: "message",
                    content: "Analysis resumed",
                });
                break;

            case "error":
                this._appendEvent({
                    type: "message",
                    content: "Error: " + data.message,
                });
                this.isThinking = false;
                break;
        }
    },

    sendUserMessage() {
        const message = this.userInput.trim();
        if (!message) return;

        if (this.useWebSocket) {
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                // WebSocket mode
                this.websocket.send(JSON.stringify({
                    type: "user_message",
                    message: message,
                }));
                // Reset plan index so new response creates a new card instead of updating the old one
                this.planIndex = -1;
            } else {
                this.$message.warning("WS 未连接");
                return;
            }
        } else {
            // SSE mode - just show as local message (no backend support)
            this._appendEvent({
                type: "user_input",
                content: message,
            });
            this.$message.info("SSE mode does not support human instruction intervention");
        }

        // Clear input
        this.userInput = "";
    },

    togglePause() {
        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            return;
        }

        if (this.isPaused) {
            this.websocket.send(JSON.stringify({ type: "resume" }));
        } else {
            this.websocket.send(JSON.stringify({ type: "pause" }));
        }
    },

    cancelAnalysis() {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(JSON.stringify({ type: "cancel" }));
        }
    },

    closeWebSocket() {
        if (this.websocket) {
            this.websocket.close();
            this.websocket = null;
        }
        this.sessionId = null;
    },

    _getWebSocketUrl() {
        const override =
            (typeof window !== "undefined" && window.__AI_WS_URL__) || "";
        if (override) {
            return String(override);
        }

        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const host = window.location.hostname || "127.0.0.1";

        if (process.env.NODE_ENV === "development") {
            return `${protocol}//${host}:8001/ws/analyze`;
        }

        // Production: use relative path
        const port = window.location.port ? `:${window.location.port}` : "";
        return `${protocol}//${host}${port}/ws/analyze`;
    },


    _buildAnalyzePayload() {
        const alertMsg =
            this.alertInfo.alert_msg ||
            this.alertInfo.alertitem_desc ||
            this.alertInfo.alertitem_code ||
            "";
        const hostIp = this.alertInfo.hostip || this.alertInfo.server_ip || "";
        const details = this._buildServerDetails();
        const payload = {
            alert_msg: alertMsg,
            host_ip: hostIp,
        };
        if (details) {
            payload.server_details = details;
        }
        if (this.shellToken) {
            payload.shell_token = this.shellToken;
        }
        return payload;
    },

    _buildServerDetails() {
        const parts = [];
        if (this.alertInfo.hostname) {
            parts.push("hostname: " + this.alertInfo.hostname);
        }
        if (this.alertInfo.project) {
            parts.push("project: " + this.alertInfo.project);
        }
        if (this.alertInfo.server_busi_name) {
            parts.push("service: " + this.alertInfo.server_busi_name);
        }
        if (this.alertInfo.alertitem_desc) {
            parts.push("description: " + this.alertInfo.alertitem_desc);
        }
        return parts.join("\n");
    },

    _safeJsonParse(text) {
        if (!text) {
            return "";
        }
        try {
            return JSON.parse(text);
        } catch (err) {
            return text;
        }
    },

    _consumeJsonFragment(text) {
        if (!text || typeof text !== "string") {
            return false;
        }
        if (this.jsonStreamActive || this._looksLikeJsonFragment(text)) {
            this.jsonStreamActive = true;
            this.jsonStreamBuffer += text;
            this._processJsonBuffer();
            return true;
        }
        return false;
    },

    _processJsonBuffer() {
        const buffer = this.jsonStreamBuffer;
        if (!buffer) {
            this.jsonStreamActive = false;
            return;
        }
        const payloads = this._extractJsonObjects(buffer);
        if (payloads.length) {
            payloads.forEach((payload) => {
                if (this._tryHandlePlanPayload(payload)) {
                    return;
                }
                this._handleToolPayloadObject(payload);
            });
            const lastBrace = buffer.lastIndexOf("}");
            if (lastBrace !== -1) {
                const remainder = buffer.slice(lastBrace + 1);
                const cleaned = this._stripJsonControlTail(remainder);
                if (cleaned && !this._looksLikeJsonFragment(cleaned)) {
                    this.jsonStreamBuffer = "";
                    this.jsonStreamActive = false;
                    this._appendMessageDelta(cleaned);
                    return;
                }
                this.jsonStreamBuffer = cleaned;
            }
        }
        if (this.jsonStreamBuffer.length > 8000) {
            this.jsonStreamBuffer = "";
            this.jsonStreamActive = false;
            return;
        }
        if (!this.jsonStreamBuffer.trim()) {
            this.jsonStreamActive = false;
        }
    },

    _handleJsonTextMessage(text) {
        if (!text || typeof text !== "string") {
            return false;
        }
        const trimmed = text.trim();
        // Try parsing assuming it might be an escaped string first
        if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
            try {
                 const unescaped = JSON.parse(trimmed);
                 if (typeof unescaped === 'string' && (this._looksLikeJsonText(unescaped) || this._looksLikeJsonFragment(unescaped))) {
                     // Recursively handle the unescaped text
                     if (this._handleJsonTextMessage(unescaped)) {
                         return true;
                     }
                 }
            } catch (e) {
                // ignore
            }
        }
        
        if (!trimmed || !this._looksLikeJsonText(trimmed)) {
            return false;
        }
        const payloads = this._extractJsonObjects(trimmed);
        if (payloads.length) {
            payloads.forEach((payload) => {
                if (this._tryHandlePlanPayload(payload)) {
                    return;
                }
                this._handleToolPayloadObject(payload);
            });
        }
        return true;
    },

    _stripJsonControlTail(text) {
        if (!text) {
            return "";
        }
        let cleaned = String(text);
        cleaned = cleaned.replace(/```(?:json)?/gi, "");
        cleaned = cleaned.replace(/begin strict json output/gi, "");
        cleaned = cleaned.replace(/end strict json output/gi, "");
        return cleaned.trim();
    },

    _tryHandlePlanPayload(payload) {
        if (!payload || typeof payload !== "object") {
            return false;
        }
        
        // Check for Analysis Plan structure
        // Keys: analysis_summary, analysis_trace, steps
        // Allow partial matches but Require at least one distinctive key
        if (payload.analysis_summary || (payload.steps && Array.isArray(payload.steps)) || payload.analysis_trace) {
            const plan = this._buildPlanEvent(payload);
            if (plan) {
                this._appendPlanEvent(plan);
                return true;
            }
        }
        
        // Also check if it's wrapped in a "content" or "output" that IS the plan
        // (Sometimes LLM wraps it: { "content": { "analysis_summary": ... } })
        if (payload.content && typeof payload.content === 'object') {
             if (this._tryHandlePlanPayload(payload.content)) {
                 return true;
             }
        }
        
        return false;
    },

    _handleToolPayloadObject(payload) {
        if (!payload || typeof payload !== "object") {
            return false;
        }
        const name = this._getToolPayloadName(payload);
        if (!name) {
            return false;
        }
        const input = this._getToolPayloadInput(payload);
        const output = this._getToolPayloadOutput(payload);
        const hasToolMarkers =
            Boolean(input) || Boolean(output) || Boolean(payload.id);
        if (!hasToolMarkers) {
            return false;
        }
        if (this._isStructuredOutputToolName(name)) {
            return true;
        }
        if (output) {
            const outputText = this._formatToolOutputPayload(output);
            if (outputText) {
                const status = payload.is_error || payload.error ? "failed" : "success";
                const index = this._findLastToolCallIndex();
                if (index !== -1) {
                    this.$set(this.eventStream[index], "status", status);
                    this.$set(this.eventStream[index], "output", outputText);
                }
                this._appendEvent({
                    type: "tool_output",
                    tool: name,
                    content: outputText,
                });
            }
            return true;
        }
        if (input) {
            const inputText = this._formatToolInput({ input: input });
            if (inputText) {
                this._appendEvent({
                    type: "tool_call",
                    tool: name,
                    input: inputText,
                    status: "pending",
                });
                this.lastToolIndex = this.eventStream.length - 1;
            }
            return true;
        }
        return true;
    },

    _getToolPayloadName(payload) {
        if (!payload || typeof payload !== "object") {
            return "";
        }
        if (payload.tool) {
            return String(payload.tool);
        }
        if (payload.name) {
            return String(payload.name);
        }
        return "";
    },

    _getToolPayloadInput(payload) {
        if (!payload || typeof payload !== "object") {
            return null;
        }
        if (payload.input) {
            return payload.input;
        }
        if (payload.parameters) {
            return payload.parameters;
        }
        if (payload.params) {
            return payload.params;
        }
        return null;
    },

    _getToolPayloadOutput(payload) {
        if (!payload || typeof payload !== "object") {
            return null;
        }
        if (payload.output) {
            return payload.output;
        }
        if (payload.result) {
            return payload.result;
        }
        if (payload.content) {
            return payload.content;
        }
        if (payload.data) {
            return payload.data;
        }
        return null;
    },

    _formatToolOutputPayload(output) {
        if (output == null) {
            return "";
        }
        if (typeof output === "string") {
            return output;
        }
        if (Array.isArray(output)) {
            const parts = [];
            output.forEach((item) => {
                if (item && typeof item === "object") {
                    if (item.type === "text" && item.text) {
                        parts.push(String(item.text));
                        return;
                    }
                    try {
                        parts.push(JSON.stringify(item));
                    } catch (err) {
                        parts.push(String(item));
                    }
                    return;
                }
                parts.push(String(item));
            });
            return parts.join("\n");
        }
        try {
            return JSON.stringify(output);
        } catch (err) {
            return String(output);
        }
    },

    _isStructuredOutputToolName(name) {
        if (!name) {
            return false;
        }
        const lower = String(name).toLowerCase();
        return (
            lower === "structuredoutput" ||
            lower.endsWith("structuredoutput") ||
            lower.includes("structured_output")
        );
    },

    _looksLikeJsonText(text) {
        if (!text) {
            return false;
        }
        const trimmed = text.trim();
        if (!trimmed) {
            return false;
        }
        const first = trimmed.charAt(0);
        if (first === "{" || first === "[") {
            return true;
        }
        return this._looksLikeJsonFragment(trimmed);
    },

    _looksLikeJsonFragment(text) {
        if (!text) {
            return false;
        }
        if (text.indexOf("{") !== -1 || text.indexOf("}") !== -1) {
            return true;
        }
        if (text.indexOf("\"name\":") !== -1) {
            return true;
        }
        // Basic Checks
        if (text.indexOf("\"analysis_summary\"") !== -1) return true;
        if (text.indexOf("\"analysis_trace\"") !== -1) return true;
        if (text.indexOf("\"steps\"") !== -1) return true;
        if (text.indexOf("\"parameters\"") !== -1) return true;
        
        // Escaped Checks (Fix for raw JSON rendering issue)
        // Checks for \"analysis_summary\" which indicates double-encoded JSON
        if (text.indexOf('\\"analysis_summary\\"') !== -1) return true;
        if (text.indexOf('\\"analysis_trace\\"') !== -1) return true;
        if (text.indexOf('\\"steps\\"') !== -1) return true;
        if (text.indexOf('\\"parameters\\"') !== -1) return true;

        if (text.indexOf("StructuredOutput") !== -1 || text.indexOf("structured_output") !== -1) {
            return true;
        }
        if (text.indexOf("mcp__") !== -1) {
            return true;
        }
        const lower = text.toLowerCase();
        if (lower.indexOf("begin strict json output") !== -1) {
            return true;
        }
        if (lower.indexOf("strict json output") !== -1) {
            return true;
        }
        if (lower.indexOf("```json") !== -1) {
            return true;
        }
        if (lower.indexOf("found") !== -1 && lower.indexOf("tools") !== -1 && lower.indexOf("required keys") !== -1) {
            return true;
        }
        return false;
    },

    _extractJsonObjects(text) {
        const results = [];
        if (!text) {
            return results;
        }
        let depth = 0;
        let start = -1;
        let inString = false;
        let escaping = false;

        for (let i = 0; i < text.length; i += 1) {
            const ch = text[i];
            if (inString) {
                if (escaping) {
                    escaping = false;
                    continue;
                }
                if (ch === "\\") {
                    escaping = true;
                    continue;
                }
                if (ch === "\"") {
                    inString = false;
                }
                continue;
            }

            if (ch === "\"") {
                inString = true;
                continue;
            }
            if (ch === "{") {
                if (depth === 0) {
                    start = i;
                }
                depth += 1;
                continue;
            }
            if (ch === "}") {
                if (depth > 0) {
                    depth -= 1;
                    if (depth === 0 && start !== -1) {
                        const snippet = text.slice(start, i + 1);
                        try {
                            const parsed = JSON.parse(snippet);
                            if (parsed && typeof parsed === "object") {
                                results.push(parsed);
                            }
                        } catch (err) {
                            // ignore invalid fragments
                        }
                        start = -1;
                    }
                }
            }
        }
        return results;
    },

    _normalizeMessageDelta(data) {
        if (!data) {
            return "";
        }
        if (typeof data === "string") {
            return data;
        }
        if (typeof data === "object" && data.text) {
            return String(data.text);
        }
        return "";
    },

    _writeTerminalOutput(output) {
        if (!this.$refs.shellTerm || !this.$refs.shellTerm.target_term) return;
        const term = this.$refs.shellTerm.target_term;
        const normalized = String(output).replace(/\n/g, "\r\n");
        term.write("\r\n" + normalized + "\r\n");
    },

    // --- Core Agent Logic (Mocked for Demo) ---
    async startMockAnalysis() {
        this.isThinking = true;
        
        // 1. Connect to Shell First (Pre-requisite for Agent)
        await this.connectShell(); 
        
        // 2. Start the Event Stream
        const scenario = [
            { type: 'thought', content: '收到告警：CPU 使用率过高 (95%)。正在加载《Linux CPU 排查手册》...' },
            { delay: 1000 },
            { type: 'message', content: '检测到 CPU 负载异常，我将开始排查运行队列及 Top 进程。' },
            { delay: 800 },
            { type: 'thought', content: '决定执行 `top` 命令查看系统概况。' },
            { delay: 500 },
            { type: 'tool_call', tool: 'exec_shell', input: 'top -b -n 1 | head -n 15', status: 'running' }, 
            { delay: 2000, action: 'simulate_output_top' }, // Simulate heavy output
            { type: 'update_tool', status: 'success' },
            { delay: 1000 },
            { type: 'thought', content: '观测到 PID 8080 (java) 占用 200% CPU。需要进一步查看该进程堆栈。' },
            { delay: 1000 },
            { type: 'message', content: '发现异常进程 Java (PID 8080)。我需要获取线程堆栈信息来定位具体代码段。' },
            { delay: 1500 },
            { type: 'tool_call', tool: 'exec_shell', input: 'jstack 8080 | head -n 20', status: 'running' },
            { delay: 2000, action: 'simulate_output_jstack' },
            { type: 'update_tool', status: 'success' },
            { delay: 1000 },
            { type: 'thought', content: '堆栈显示大量线程阻塞在 PaymentService.calculate() 方法。疑似死锁。' },
            { delay: 500 },
            { type: 'message', content: '⚠️ 定位完成：PaymentService 出现死锁，导致 CPU 飙升。建议重启服务或回滚版本。' },
            { type: 'finish' }
        ];

        this.runScenario(scenario);
    },

    async runScenario(steps) {
        let lastToolIndex = -1;

        for (const step of steps) {
            if (!this.visible) return; // Stop if closed

            if (step.delay) {
                if (step.action) {
                    await this.executeMockAction(step.action);
                }
                await new Promise(r => setTimeout(r, step.delay));
                continue;
            }
            
            if (step.type === 'finish') {
                this.isThinking = false;
                continue;
            }

            if (step.type === 'update_tool') {
                if (lastToolIndex >= 0) {
                    // Update the last tool call status
                    this.$set(this.eventStream[lastToolIndex], 'status', step.status);
                }
                continue;
            }

            // Normal Event Push
            this.eventStream.push(step);
            
             // Scroll to bottom
            this.$nextTick(() => {
                const container = this.$refs.streamContent;
                if(container) container.scrollTop = container.scrollHeight;
            });

            if (step.type === 'tool_call') {
                lastToolIndex = this.eventStream.length - 1;
                // Visualize Typing in Terminal
                await this.typeInTerminal(step.input);
            }
        }
    },

    // --- Terminal Interactions ---
    async connectShell() {
        // Real logic to connect to webshell
         try {
            const hostIp = this.alertInfo.hostip || this.alertInfo.server_ip || '';
            const project =
                this.alertInfo.project ||
                this.alertInfo.project_code ||
                this.alertInfo.project_name ||
                '';
            const preferredUser =
                (this.alertInfo.ai_user_name || this.alertInfo.ai_user || this.alertInfo.user_name || '').trim();

            if (!hostIp || !project) {
                this.$message.warning('缺少主机IP或项目参数，无法查询用户');
                return;
            }

            const userRes = await API.GET('/server/ai_query_user', {
                hostip: hostIp,
                project: project,
                data_type: 'omnis'
            });

            let selectedUser = null;
            let serverId = '';
            if (userRes.data && userRes.data.success) {
                 const payload = userRes.data.data || {};
                 const servers = Array.isArray(payload.servers) ? payload.servers : [];
                 const users = Array.isArray(payload.users) ? payload.users : [];
                 this.userQueryEvidence = payload;

                 if (!servers.length) {
                     this.$message.warning('未找到匹配的主机信息，请确认 hostip 与项目一致');
                     return;
                 }

                 if (preferredUser) {
                     selectedUser = users.find(u => u.user_name === preferredUser) || null;
                     if (!selectedUser) {
                         this.$message.warning('未配置用户：' + preferredUser);
                         return;
                     }
                 }

                 if (!selectedUser) {
                     selectedUser = users.find(u => u.user_name === 'root') || users[0] || null;
                 }

                 if (!selectedUser) {
                     this.$message.warning('该主机未配置可用用户');
                     return;
                 }

                 serverId = selectedUser.server_id || (servers[0] && servers[0].server_id) || '';
                 if (!serverId) {
                     this.$message.warning('未找到服务器ID，无法登录终端');
                     return;
                 }
            } else {
                this.$message.warning('查询用户失败');
                return;
            }

            const loginRes = await API.POST('/omnis/fort_service/webssh_login', {
                server_id: serverId,
                server_os_user_id: selectedUser.server_os_user_id,
                password: ''
            });
            
            if (loginRes.data && loginRes.data.success) {
                this.shellToken = loginRes.data.data;
                this.isConnected = true;
            }
         } catch(e) {
             console.error("Connect failed", e);
             // Fallback for UI demo
             this.isConnected = true;
         }
    },

    async typeInTerminal(command) {
        if (!this.$refs.shellTerm || !this.$refs.shellTerm.target_term) return;
        const term = this.$refs.shellTerm.target_term;
        
        term.write('\r\n$ ');
        
        // Ghost typing effect
        for (let char of command) {
            term.write(char);
            await new Promise(r => setTimeout(r, 30 + Math.random() * 50)); // Random typing speed
        }
        term.write('\r\n'); // Enter
    },
    
    async executeMockAction(actionType) {
        if (!this.$refs.shellTerm || !this.$refs.shellTerm.target_term) return;
        const term = this.$refs.shellTerm.target_term;

        if (actionType === 'simulate_output_top') {
             const output = `
top - 10:00:01 up 10 days,  2:03,  1 user,  load average: 2.00, 1.98, 1.95
Tasks: 120 total,   1 running, 119 sleeping,   0 stopped,   0 zombie
%Cpu(s): 95.0 us,  2.0 sy,  0.0 ni,  3.0 id,  0.0 wa,  0.0 hi,  0.0 si,  0.0 st
MiB Mem :   7950.0 total,   1200.0 free,   5000.0 used,   1750.0 buff/cache
MiB Swap:      0.0 total,      0.0 free,      0.0 used.   2500.0 avail Mem 

  PID USER      PR  NI    VIRT    RES    SHR S  %CPU  %MEM     TIME+ COMMAND                                                                                                        
 8080 root      20   0 8500124   2.5g  21000 S 200.0  32.0 150:20.12 java                                                                                                           
  123 root      20   0  120000   5000   3000 S   0.5   0.1   0:01.23 sshd                                                                                                           
    1 root      20   0  150000   8000   4000 S   0.0   0.1   0:05.12 systemd                                                                                                        
`;
            // Write chunks to simulate streaming
            term.write(output.replace(/\n/g, '\r\n'));
        }
        
        if (actionType === 'simulate_output_jstack') {
            const output = `
"http-nio-8080-exec-1" #25 daemon prio=5 os_prio=0 tid=0x00007f... nid=0x1a03 waiting for monitor entry [0x00007f...]
   java.lang.Thread.State: BLOCKED (on object monitor)
    at com.omnis.service.PaymentService.calculate(PaymentService.java:125)
    - waiting to lock <0x00000006c0000000> (a java.lang.Object)
    at com.omnis.controller.PayController.doPay(PayController.java:50)
    
"http-nio-8080-exec-2" #26 daemon prio=5 os_prio=0 tid=0x00007f... nid=0x1a04 waiting for monitor entry [0x00007f...]
   java.lang.Thread.State: BLOCKED (on object monitor)
`;
            term.write(output.replace(/\n/g, '\r\n'));
        }
    },

    stopMockAnalysis() {
        this.stopStream();
    },
    
    closeShell() {
         if (this.shellToken) {
            API.DELETE('/omnis/fort_service/webssh_login', { token: this.shellToken });
            this.shellToken = "";
            this.isConnected = false;
        }
    }
  },
};
</script>

<style scoped>
/* Dark/Modern Theme for Agent Console */
.agent-container {
  display: flex;
  height: 70vh;
  gap: 0; 
  background: #1e1e1e; /* VS Code-like Dark */
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  overflow: hidden;
}

/* --- Left Stream Panel --- */
.agent-stream-panel {
  flex: 1; /* 33% width roughly */
  max-width: 450px;
  min-width: 350px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #333;
  background: #252526;
}

.stream-header {
  padding: 15px;
  border-bottom: 1px solid #333;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #2d2d2d;
}

.agent-avatar {
    width: 36px;
    height: 36px;
    background: #0e639c; /* VS Code Blue */
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.agent-status .name {
    font-weight: bold;
    font-size: 14px;
    color: #fff;
}

.agent-status .status-text {
    font-size: 12px;
    color: #4ec9b0; /* Active color */
    margin-top: 2px;
}
.agent-status .status-text.idle {
    color: #666;
}

.stream-content {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

/* Event Items */
.agent-thought {
    display: flex;
    gap: 8px;
    color: #858585;
    font-size: 13px;
    font-style: italic;
    padding: 4px 8px;
    background: #2d2d2d;
    border-radius: 4px;
}

.agent-message {
    display: flex;
    justify-content: flex-start;
}
.message-bubble {
    background: #3c3c3c;
    color: #e0e0e0;
    padding: 10px 14px;
    border-radius: 8px 8px 8px 0;
    line-height: 1.4;
    font-size: 13px;
    white-space: pre-wrap;
}

.agent-action {
    margin: 4px 0;
}
.action-card {
    background: #1e1e1e;
    border: 1px solid #3e3e42;
    border-radius: 4px;
    overflow: hidden;
}
.action-header {
    background: #333333;
    padding: 6px 10px;
    font-size: 12px;
    color: #cccccc;
    display: flex;
    align-items: center;
    gap: 6px;
}
.action-code {
    padding: 10px;
    background: #1e1e1e;
    font-family: 'Consolas', monospace;
    font-size: 12px;
    color: #9cdcfe;
    border-bottom: 1px solid #333;
}
.action-status {
    padding: 4px 10px;
    font-size: 11px;
    display: flex;
    justify-content: flex-end;
}
.action-actions {
    padding: 6px 10px 10px;
    display: flex;
    justify-content: flex-end;
}
.status-pending { color: #9cdcfe; }
.status-running { color: #dcdcaa; }
.status-success { color: #6a9955; }
.status-failed { color: #f48771; }

.agent-tool-output {
    background: #1e1e1e;
    border: 1px solid #3e3e42;
    border-radius: 4px;
    overflow: hidden;
}
.output-header {
    background: #333333;
    padding: 6px 10px;
    font-size: 12px;
    color: #cccccc;
    display: flex;
    align-items: center;
    gap: 6px;
}
.output-body {
    margin: 0;
    padding: 10px;
    font-size: 12px;
    color: #dcdcdc;
    background: #1e1e1e;
    white-space: pre-wrap;
}

.input-area {
    padding: 10px;
    background: #252526;
    border-top: 1px solid #333;
}

/* User Input Message (Human Instruction) */
.user-input-message {
    display: flex;
    justify-content: flex-end;
    margin: 8px 0;
}

.user-input-message .message-bubble.user {
    background: #0e639c;
    color: white;
    padding: 10px 14px;
    border-radius: 8px 8px 0 8px;
    max-width: 80%;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.user-input-message .message-bubble.user i {
    font-size: 14px;
}

/* --- Right Terminal Panel --- */
.terminal-panel {
  flex: 2;
  display: flex;
  flex-direction: column;
  background: #000;
  position: relative;
}

.panel-header {
  height: 40px;
  background: #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px;
  color: #ccc;
  font-size: 13px;
}

.terminal-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.terminal-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
}
.overlay-content {
    color: #888;
    font-size: 14px;
}

/* Plan Card Styles */
.agent-plan {
    margin: 8px 0;
}
.plan-card {
    background: #1e1e1e;
    border: 1px solid #454545;
    border-radius: 6px;
    overflow: hidden;
}
.plan-header {
    background: #007acc;
    color: white;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 8px;
}
.plan-body {
    padding: 12px;
}
.plan-summary {
    font-size: 13px;
    color: #d4d4d4;
    margin-bottom: 15px;
    line-height: 1.5;
    border-bottom: 1px solid #333;
    padding-bottom: 10px;
}
.plan-section-title {
    font-size: 12px;
    color: #9cdcfe;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.plan-summary-text {
    color: #d4d4d4;
    line-height: 1.5;
}
.plan-trace {
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #333;
}
.trace-item {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
}
.trace-item:last-child {
    margin-bottom: 0;
}
.trace-index {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #2d2d2d;
    color: #9cdcfe;
    font-size: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
}
.trace-content {
    flex: 1;
    font-size: 12px;
    color: #c5c5c5;
    line-height: 1.4;
}
.trace-check {
    color: #e0e0e0;
    margin-bottom: 2px;
}
.trace-evidence,
.trace-result {
    color: #9aa0a6;
}
.step-item {
    display: flex;
    gap: 10px;
    margin-bottom: 12px;
}
.step-item:last-child {
    margin-bottom: 0;
}
.step-num {
    background: #333;
    color: #ccc;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
    margin-top: 2px;
}
.step-content {
    flex: 1;
}
.step-title {
    font-size: 13px;
    color: #e0e0e0;
    font-weight: 500;
    margin-bottom: 4px;
}
.step-cmd code {
    display: block;
    background: #2d2d2d;
    color: #ce9178;
    padding: 4px 6px;
    border-radius: 3px;
    font-family: 'Consolas', monospace;
    font-size: 12px;
    margin-bottom: 4px;
}
.step-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 6px;
}
.step-desc {
    font-size: 12px;
    color: #858585;
}
</style>

<style>
/* Override Element UI dialog styles for this specific dialog to match dark theme */
.agent-dialog .el-dialog__header {
    background: #252526;
    padding: 15px;
    border-bottom: 1px solid #333;
}
.agent-dialog .el-dialog__title {
    color: #fff;
    font-size: 16px;
}
.agent-dialog .el-dialog__body {
    padding: 0; /* Full bleed */
    background: #1e1e1e;
}
.agent-dialog .el-dialog__close {
    color: #fff;
}
</style>



// WEBPACK FOOTER //
// AiAnalysisDialog.vue?7dbb6fea